import { Component, useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "@/App.css";
import SmoothScroll from "./components/SmoothScroll";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Services from "./pages/Services";
import AccountingBookkeeping from "./pages/AccountingBookkeeping";
import TaxPlanning from "./pages/TaxPlanning";
import TaxBusiness from "./pages/TaxBusiness";
import TaxPersonal from "./pages/TaxPersonal";
import About from "./pages/About";
import Resources from "./pages/Resources";
import ChecklistBusiness from "./pages/ChecklistBusiness";
import ChecklistPersonal from "./pages/ChecklistPersonal";
import ChangingBookkeepers from "./pages/ChangingBookkeepers";
import Faq from "./pages/Faq";
import Contact from "./pages/Contact";
import BookOnline from "./pages/BookOnline";
import BookingCalendarRedirect from "./components/BookingCalendarRedirect";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Disclosures from "./pages/Disclosures";
import Admin from "./pages/Admin";
import NotFound from "./pages/NotFound";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="mx-auto max-w-xl px-6 py-40 text-center">
          <h1 className="font-serif text-4xl text-olive">Something went wrong</h1>
          <p className="mt-4 text-moss">Please refresh the page. If the problem persists, call us at (801) 474-5890.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function Backdrop() {
  return (
    <div aria-hidden data-testid="sunset-backdrop" className="pointer-events-none fixed inset-0 z-0">
      <img src="/img/photo-sunset-band.webp" alt="" width="628" height="286" className="h-full w-full scale-105 object-cover blur-[2px]" style={{ objectPosition: "center 60%" }} />
      <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-white/88 to-sage/90" />
    </div>
  );
}

// Records every tap on a phone link (action: phone_call) so the dashboard can
// show which pages generate the most calls.
function PhoneCallTracker() {
  useEffect(() => {
    const handler = (e) => {
      const a = e.target && e.target.closest ? e.target.closest('a[href^="tel:"]') : null;
      if (!a) return;
      try {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "phone_call",
            page: `${window.location.pathname} ${a.getAttribute("data-testid") || ""}`.trim(),
          }),
          keepalive: true,
        }).catch(() => {});
      } catch (err) {
        /* tracking must never block the call */
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);
  return null;
}

export default function App() {
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <BrowserRouter>
          <Backdrop />
          <PhoneCallTracker />
          <SmoothScroll>
            <div className="relative z-10">
              <Header />
              <main id="main">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/services" element={<Services />} />
                <Route path="/services/accounting-bookkeeping" element={<AccountingBookkeeping />} />
                <Route path="/tax-planning" element={<TaxPlanning />} />
                <Route path="/tax-planning/business" element={<TaxBusiness />} />
                <Route path="/tax-planning/personal" element={<TaxPersonal />} />
                <Route path="/about" element={<About />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/resources/business-accounting-checklist" element={<ChecklistBusiness />} />
                <Route path="/resources/personal-tax-checklist" element={<ChecklistPersonal />} />
                <Route path="/resources/changing-bookkeepers" element={<ChangingBookkeepers />} />
                <Route path="/faq" element={<Faq />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/book-online" element={<BookOnline />} />
                <Route path="/booking-calendar/*" element={<BookingCalendarRedirect />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/disclosures" element={<Disclosures />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
              <Footer />
            </div>
          </SmoothScroll>
        </BrowserRouter>
      </ErrorBoundary>
    </HelmetProvider>
  );
}
